import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Edit3, Eye, Folder, FolderOpen, GripVertical, History, Info, Loader2, Lock, Plus, Save, Search, Trash2, X } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  adminBinderTemplates,
  adminEisf,
  myPermissions,
  parseMappedDocTypeCodes,
  type BinderTemplateDetail,
  type BinderTemplateFolderRow,
  type BinderTemplateReadiness,
  type DocumentTypeWithApprovers,
  type MandatoryDocument,
  type MasterFolderTemplateRow,
} from '@/api/client';
import { approvalSummary, FolderApprovalDialog } from './FolderApprovalDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { BlindingScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { ConfirmModal } from '@/components/layout/ConfirmModal';
import { fmtDateTime } from '@/lib/dates';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Drop-position derived on drag-over. Encodes where the dragged folder
 * would land relative to the folder currently under the pointer.
 *   'above' -> insert as sibling BEFORE targetId (same parent as targetId)
 *   'into'  -> become a child of targetId (last position)
 *   'below' -> insert as sibling AFTER targetId (same parent as targetId)
 *   'root'  -> special sentinel: drop onto the root zone at the very top
 */
type DropPosition = 'above' | 'into' | 'below' | 'root';

/**
 * F8b - Editor for a single binder template.
 *
 * Two panes:
 *   Left  - template header (name, description, is_active)
 *   Right - folder tree, built from folders + parent_template_folder_id
 *
 * The default template is rendered read-only; the BE would refuse writes
 * anyway, but visually locking the UI avoids a surprise error toast.
 *
 * Re-parenting (drag-and-drop) is deferred; today's UI supports rename +
 * add + delete, plus a manual "parent picker" dropdown on each folder row
 * for admins who need to restructure without waiting for the DnD ticket.
 */
export function BinderTemplateEditorPage() {
  const { id: templateId } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['adminBinderTemplates', 'detail', templateId],
    queryFn: () => adminBinderTemplates.get(templateId!),
    enabled: !!templateId,
  });

  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({ name: '', description: '' });
  const [addingFolder, setAddingFolder] = useState<null | { parentId: string | null }>(null);
  const [confirmingFolderDelete, setConfirmingFolderDelete] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const template = detailQ.data?.template;
  // Caspio returns "" (empty string) for null STRING fields, not real nulls.
  // If we let that leak into the tree builder, root folders whose
  // parent_template_folder_id is "" end up keyed under "" instead of null,
  // and buildTree's build(null) call finds zero roots -> tree renders empty
  // even though folders.length > 0. Same story for display_name_override:
  // an empty override "wins" over the master's artifact_type in the
  // ?? fallback, so folders would render blank. Normalise once here so
  // downstream code can rely on real nulls.
  const folders = useMemo(
    () =>
      (detailQ.data?.folders ?? []).map((f) => ({
        ...f,
        parent_template_folder_id:
          f.parent_template_folder_id && f.parent_template_folder_id.length > 0
            ? f.parent_template_folder_id
            : null,
        display_name_override:
          f.display_name_override && f.display_name_override.length > 0
            ? f.display_name_override
            : null,
      })),
    [detailQ.data?.folders],
  );
  const masters = detailQ.data?.masters ?? [];
  const mandatoryArtifactTypes = useMemo(
    () => new Set(detailQ.data?.mandatory_artifact_types ?? []),
    [detailQ.data?.mandatory_artifact_types],
  );
  const permsQ = useQuery({ queryKey: ['myPermissions'], queryFn: () => myPermissions.get() });
  const ops = permsQ.data?.operations ?? [];
  const isReadOnly =
    (template?.is_default === 'true' && !ops.includes('master_data.binder_templates_edit_default'))
    || template?.is_active !== 'true';

  const mastersById = useMemo(() => {
    const m = new Map<string, MasterFolderTemplateRow>();
    masters.forEach((row) => m.set(row.template_id, row));
    return m;
  }, [masters]);

  const usedMasterIds = useMemo(
    () => new Set(folders.map((f) => f.master_folder_template_id).filter(Boolean) as string[]),
    [folders],
  );

  const docTypesQ = useQuery({
    queryKey: ['adminEisf', 'documentTypes'],
    queryFn: () => adminEisf.listDocumentTypes(),
  });
  const docTypesByCode = useMemo(() => {
    const m = new Map<string, DocumentTypeWithApprovers>();
    (docTypesQ.data?.document_types ?? []).forEach((t) => m.set(t.document_type_code, t));
    return m;
  }, [docTypesQ.data?.document_types]);

  const canEditApproval =
    !isReadOnly
    && ops.includes('master_data.doc_types_write')
    && ops.includes('master_data.binder_templates_write');

  const [approvalFor, setApprovalFor] = useState<string | null>(null);


  const setFolderDocTypeM = useMutation({
    mutationFn: async ({ folderId, code }: { folderId: string; code: string | null }) => {
      const holder = code
        ? folders.find(
            (f) =>
              f.template_folder_id !== folderId && parseMappedDocTypeCodes(f).includes(code),
          )
        : undefined;
      if (holder) {
        await adminBinderTemplates.updateFolder(templateId!, holder.template_folder_id, {
          mapped_doc_type_codes: parseMappedDocTypeCodes(holder).filter((c) => c !== code),
        });
      }
      const current = parseMappedDocTypeCodes(
        folders.find((f) => f.template_folder_id === folderId),
      );
      const rest = current.slice(1).filter((c) => c !== code);
      await adminBinderTemplates.updateFolder(templateId!, folderId, {
        mapped_doc_type_codes: code ? [code, ...rest] : rest,
      });
      return { folderId, code, holderId: holder?.template_folder_id ?? null };
    },
    onSuccess: ({ folderId, code, holderId }) => {
      qc.setQueryData(
        ['adminBinderTemplates', 'detail', templateId],
        (prev: BinderTemplateDetail | undefined) => {
          if (!prev) return prev;
          const nextFolders = prev.folders.map((f) => {
            if (f.template_folder_id === holderId && code) {
              const left = parseMappedDocTypeCodes(f).filter((c) => c !== code);
              return { ...f, mapped_doc_type_codes: left.length ? JSON.stringify(left) : null };
            }
            if (f.template_folder_id === folderId) {
              const rest = parseMappedDocTypeCodes(f).slice(1).filter((c) => c !== code);
              const next = code ? [code, ...rest] : rest;
              return { ...f, mapped_doc_type_codes: next.length ? JSON.stringify(next) : null };
            }
            return f;
          });
          return { ...prev, folders: nextFolders };
        },
      );
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Could not set the document type'));
    },
  });

  const updateHeaderM = useMutation({
    mutationFn: (patch: { name?: string; description?: string; is_active?: boolean }) =>
      adminBinderTemplates.update(templateId!, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates'] });
      toast.success('Template saved');
      setEditingHeader(false);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Save failed'));
    },
  });

  const addFolderM = useMutation({
    mutationFn: (body: {
      master_folder_template_id?: string;
      display_name_override?: string;
      parent_template_folder_id?: string | null;
    }) => adminBinderTemplates.addFolder(templateId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
      toast.success('Folder added');
      setAddingFolder(null);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Add failed'));
    },
  });

  const renameFolderM = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      adminBinderTemplates.updateFolder(templateId!, folderId, {
        display_name_override: name || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
      toast.success('Folder renamed');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Rename failed'));
    },
  });

  const setTierM = useMutation({
    mutationFn: ({ folderId, tier }: { folderId: string; tier: 'common' | 'unblinded_only' | 'general' }) =>
      adminBinderTemplates.updateFolder(templateId!, folderId, { tier }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
      toast.success('Blinding tier updated');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Tier update failed'));
    },
  });

  /**
   * CR-2026-09-29 — point a mandatory document at one of this template's
   * folders. Sent as two calls when the document is moving between folders,
   * because the backend refuses a code that another folder still claims.
   */
  const setMappingM = useMutation({
    mutationFn: async ({
      code,
      fromFolderId,
      toFolderId,
    }: {
      code: string;
      fromFolderId: string | null;
      toFolderId: string | null;
    }) => {
      if (fromFolderId) {
        const remaining = parseMappedDocTypeCodes(
          folders.find((f) => f.template_folder_id === fromFolderId),
        ).filter((c) => c !== code);
        await adminBinderTemplates.updateFolder(templateId!, fromFolderId, {
          mapped_doc_type_codes: remaining,
        });
      }
      if (toFolderId) {
        const existing = parseMappedDocTypeCodes(
          folders.find((f) => f.template_folder_id === toFolderId),
        );
        await adminBinderTemplates.updateFolder(templateId!, toFolderId, {
          mapped_doc_type_codes: [...new Set([...existing, code])],
        });
      }
    },
    // Caspio reads lag its own writes by a second or two, so an immediate
    // refetch here returns the OLD row and the dropdown snaps back to
    // "not mapped" even though the write succeeded. Apply the change we
    // know the server just made to the cache instead of re-reading it. The
    // next natural refetch reconciles.
    onSuccess: (_data, { code, fromFolderId, toFolderId }) => {
      qc.setQueryData(
        ['adminBinderTemplates', 'detail', templateId],
        (prev: BinderTemplateDetail | undefined) => {
          if (!prev) return prev;
          const nextFolders = prev.folders.map((f) => {
            if (f.template_folder_id === fromFolderId) {
              const remaining = parseMappedDocTypeCodes(f).filter((c) => c !== code);
              return {
                ...f,
                mapped_doc_type_codes: remaining.length ? JSON.stringify(remaining) : null,
              };
            }
            if (f.template_folder_id === toFolderId) {
              const next = [...new Set([...parseMappedDocTypeCodes(f), code])];
              return { ...f, mapped_doc_type_codes: JSON.stringify(next) };
            }
            return f;
          });
          const nextDocs = (prev.mandatory_documents ?? []).map((d) =>
            d.code === code ? { ...d, mapped_template_folder_id: toFolderId } : d,
          );
          const missing = nextDocs
            .filter((d) => !d.mapped_template_folder_id)
            .map((d) => ({ code: d.code, name: d.name }));
          return {
            ...prev,
            folders: nextFolders,
            mandatory_documents: nextDocs,
            readiness: { ready: missing.length === 0, missing },
          };
        },
      );
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates'], exact: true });
      toast.success('Document routing updated');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Could not update routing'));
      // The optimistic path did not run, but a failed first leg could have
      // left the pair half-applied, so pull the truth back.
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
    },
  });

  /** Re-point an existing folder at a different standard folder. */
  const remapMasterM = useMutation({
    mutationFn: ({ folderId, masterId }: { folderId: string; masterId: string | null }) =>
      adminBinderTemplates.updateFolder(templateId!, folderId, {
        master_folder_template_id: masterId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
      toast.success('Standard folder updated');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Could not change standard folder'));
    },
  });

  const moveFolderM = useMutation({
    mutationFn: ({
      folderId,
      parentId,
      sortOrder,
    }: {
      folderId: string;
      parentId: string | null;
      sortOrder?: number;
    }) =>
      adminBinderTemplates.updateFolder(templateId!, folderId, {
        parent_template_folder_id: parentId,
        ...(sortOrder !== undefined ? { sort_order: sortOrder } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
      // Toast per-move gets noisy during drags. Kept for the parent-picker
      // dropdown path only; DnD sends a summary toast from the caller.
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Move failed'));
    },
  });

  const deleteFolderM = useMutation({
    mutationFn: (folderId: string) =>
      adminBinderTemplates.deleteFolder(templateId!, folderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates', 'detail', templateId] });
      toast.success('Folder removed');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Delete failed'));
    },
  });

  function beginHeaderEdit() {
    if (!template) return;
    setHeaderDraft({ name: template.name, description: template.description });
    setEditingHeader(true);
  }

  if (!templateId) {
    return <div className="p-6 text-sm text-danger-700">Missing template id in URL.</div>;
  }

  if (detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading template…
      </div>
    );
  }
  if (detailQ.isError || !template) {
    return (
      <div className={`m-4 flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(detailQ.error)}`}>
        {isPermissionError(detailQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>
          {errorText(detailQ.error, 'Failed to load the template.')}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin/binder-templates"
        title={template.name}
        subtitle={template.description || undefined}
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => setPreviewOpen(true)}>
              <Eye className="h-4 w-4" /> Preview
            </button>
            <button type="button" className="btn-secondary" onClick={() => setHistoryOpen(true)}>
              <History className="h-4 w-4" /> History
            </button>
            {!editingHeader && !isReadOnly && (
              <button type="button" className="btn-secondary" onClick={beginHeaderEdit}>
                <Edit3 className="h-4 w-4" /> Edit
              </button>
            )}
            {!editingHeader && template.is_default !== 'true' && template.is_active !== 'true' && (
              <button
                type="button"
                className="btn-secondary"
                disabled={updateHeaderM.isPending}
                onClick={() => updateHeaderM.mutate({ is_active: true })}
              >
                Restore
              </button>
            )}
          </>
        }
      />

      <section className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {editingHeader ? (
              <div className="space-y-3">
                <div>
                  <label className="label">Name</label>
                  <input
                    type="text"
                    value={headerDraft.name}
                    maxLength={255}
                    onChange={(e) => setHeaderDraft({ ...headerDraft, name: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={headerDraft.description}
                    rows={2}
                    maxLength={4000}
                    onChange={(e) =>
                      setHeaderDraft({ ...headerDraft, description: e.target.value })
                    }
                    className="input w-full resize-none"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setEditingHeader(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() =>
                      updateHeaderM.mutate({
                        name: headerDraft.name.trim(),
                        description: headerDraft.description.trim(),
                      })
                    }
                    disabled={updateHeaderM.isPending || !headerDraft.name.trim()}
                  >
                    {updateHeaderM.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Save className="h-4 w-4" /> Save
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-[22px] font-semibold tracking-tight text-ink-primary">
                    {template.name}
                  </h1>
                  <span className="badge">{template.reference_number}</span>
                  <BlindingScopeBadge scope={template.blinding_scope} />
                  {template.is_default === 'true' && (
                    <span className="badge bg-accent-100 text-accent-700">
                      <Lock className="h-3 w-3" /> Default (read-only)
                    </span>
                  )}
                  {template.is_active !== 'true' && (
                    <span className="badge bg-danger-100 text-danger-700">Hidden</span>
                  )}
                </div>
                {template.description && (
                  <p className="mt-1 text-sm text-ink-muted">{template.description}</p>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* CR-2026-09-29 — which of THIS template's folders each mandatory
          document files into. Sits above the tree because it is the thing an
          admin must finish before the template can be used on a study.

          BLINDED TEMPLATES ONLY (client rule, 2026-10-11). A blinded study has
          two binders and only the blinded one auto-routes; the unblinded binder
          takes manual IP uploads. A document routes to one folder in one
          binder, so there is nothing here for an unblinded template to answer —
          BTP-0005 Pharmacy File was being asked to map Blank ICF, which the DIA
          Pharmacy File Index deliberately has no section for. */}
      {detailQ.data?.template.blinding_scope !== 'unblinded' && (
      <MandatoryDocumentsPanel
        documents={detailQ.data?.mandatory_documents ?? []}
        readiness={detailQ.data?.readiness}
        folders={folders}
        mastersById={mastersById}
        readOnly={isReadOnly}
        isPending={setMappingM.isPending}
        onMap={(code, fromFolderId, toFolderId) =>
          setMappingM.mutate({ code, fromFolderId, toFolderId })
        }
      />
      )}

      {/* Unblinded (pharmacy) templates: the binder's own DoA is the one
          document filed into it automatically (16 Oct 2026; everything else
          there is a manual upload). Without a folder for it, the signed DoA
          pages are silently not filed, so say so and let the admin pick one. */}
      {detailQ.data?.template.blinding_scope === 'unblinded' && (() => {
        const doaFolder = folders.find((f) => parseMappedDocTypeCodes(f).includes(DOA_CODE));
        const doc: MandatoryDocument = {
          code: DOA_CODE,
          name: "Delegation of Authority (this binder's own DoA)",
          zone_name: null,
          section_name: null,
          artifact_type: null,
          mapped_template_folder_id: doaFolder?.template_folder_id ?? null,
        };
        return (
          <MandatoryDocumentsPanel
            title="DoA folder"
            intro="The pharmacy binder's own DoA is the only document filed into it automatically; everything else is a manual upload. Choose the folder its signed pages go into."
            warning="No folder is set for the DoA. The signed DoA pages of studies built from this template will NOT be saved in their pharmacy binder."
            documents={[doc]}
            readiness={{ ready: !!doaFolder, missing: doaFolder ? [] : [{ code: DOA_CODE, name: doc.name }] }}
            folders={folders}
            mastersById={mastersById}
            readOnly={isReadOnly}
            isPending={setMappingM.isPending}
            onMap={(code, fromFolderId, toFolderId) =>
              setMappingM.mutate({ code, fromFolderId, toFolderId })
            }
          />
        );
      })()}

      {/* Folder tree */}
      <section className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">
              Folders ({folders.length})
            </h2>
            <p className="text-xs text-ink-muted">
              Double-click a name to rename. Hover a row and drag the grip to reorder or
              nest (drop above/below for sibling, onto the row for a child). Filter
              narrows the tree by name; expand / collapse controls the top-level view.
              Folders with a master anchor keep their identity so auto-file continues to
              route the right document types.
            </p>
          </div>
          {!isReadOnly && (
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => setAddingFolder({ parentId: null })}
            >
              <Plus className="h-4 w-4" /> Add root folder
            </button>
          )}
        </div>

        <FolderTree
          folders={folders}
          mastersById={mastersById}
          mandatoryArtifactTypes={mandatoryArtifactTypes}
          masters={masters}
          readOnly={isReadOnly}
          onRemapMaster={(folderId, masterId) => remapMasterM.mutate({ folderId, masterId })}
          onRename={(folderId, name) => renameFolderM.mutate({ folderId, name })}
          onMove={(folderId, parentId, sortOrder) =>
            moveFolderM.mutate({ folderId, parentId, sortOrder })
          }
          onDelete={(folderId) => setConfirmingFolderDelete(folderId)}
          onAddChild={(parentId) => setAddingFolder({ parentId })}
          onSetTier={(folderId, tier) => setTierM.mutate({ folderId, tier })}
          docTypes={docTypesQ.data?.document_types ?? []}
          docTypesByCode={docTypesByCode}
          onPickDocType={(folderId, code) => {
            const existing =
              parseMappedDocTypeCodes(folders.find((f) => f.template_folder_id === folderId))[0]
              ?? null;
            if (existing === code) return;
            setFolderDocTypeM.mutate({ folderId, code });
          }}
          onOpenApproval={(folderId) => setApprovalFor(folderId)}
        />
      </section>

      {approvalFor && (() => {
        const f = folders.find((x) => x.template_folder_id === approvalFor);
        if (!f) return null;
        let zone = f;
        while (zone.parent_template_folder_id) {
          const parent = folders.find((x) => x.template_folder_id === zone.parent_template_folder_id);
          if (!parent) break;
          zone = parent;
        }
        const code = parseMappedDocTypeCodes(f)[0] ?? null;
        return (
          <FolderApprovalDialog
            open
            onOpenChange={(b) => !b && setApprovalFor(null)}
            templateId={templateId!}
            folderId={f.template_folder_id}
            folderName={f.display_name_override ?? 'This folder'}
            zoneName={zone.display_name_override ?? f.display_name_override ?? 'Custom'}
            docType={code ? docTypesByCode.get(code) : undefined}
            canEdit={canEditApproval}
          />
        );
      })()}

      {addingFolder && !isReadOnly && (
        <AddFolderModal
          onClose={() => setAddingFolder(null)}
          parentId={addingFolder.parentId}
          folders={folders}
          masters={masters}
          usedMasterIds={usedMasterIds}
          isPending={addFolderM.isPending}
          onSubmit={(body) => addFolderM.mutate(body)}
        />
      )}

      {previewOpen && (
        <PreviewDrawer
          templateName={template.name}
          folders={folders}
          mastersById={mastersById}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {historyOpen && (
        <HistoryDrawer
          templateId={template.template_id}
          templateName={template.name}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {confirmingFolderDelete && (
        <ConfirmModal
          title="Remove folder"
          body={
            <p>
              Remove this folder from this template? Other templates and existing study binders
              keep their copies — this only affects future binders built from this template.
            </p>
          }
          confirmLabel={deleteFolderM.isPending ? 'Removing…' : 'Remove'}
          isPending={deleteFolderM.isPending}
          onCancel={() => setConfirmingFolderDelete(null)}
          onConfirm={() => {
            const id = confirmingFolderDelete;
            deleteFolderM.mutate(id, {
              onSuccess: () => setConfirmingFolderDelete(null),
            });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FolderTree with drag-and-drop
// ---------------------------------------------------------------------------
//
// UX contract:
//   - Grip handle on each row starts a drag.
//   - While dragging, hover over another folder to reveal a drop indicator:
//       - a horizontal blue LINE above/below the target = "insert as sibling"
//       - a blue OUTLINE around the target = "make child of this folder"
//     Which mode fires is decided from pointer-y within the target rect:
//     top third -> above, bottom third -> below, middle third -> into.
//   - A dedicated "Drop here to move to root" strip sits at the very top of
//     the tree so a nested folder can be promoted back to root.
//   - Cycle guard: dragging a folder over its own descendant is a no-op.
//   - On drop, one PATCH updates BOTH parent_template_folder_id AND
//     sort_order (fractional indexing keeps the operation O(1) with no
//     sibling re-normalise most of the time).
//   - The parent-picker dropdown is kept as a keyboard-accessible fallback.

interface TreeNode {
  folder: BinderTemplateFolderRow;
  children: TreeNode[];
}

function buildTree(folders: BinderTemplateFolderRow[]): TreeNode[] {
  const byParent = new Map<string | null, BinderTemplateFolderRow[]>();
  for (const f of folders) {
    const key = f.parent_template_folder_id ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(f);
  }
  for (const [, arr] of byParent) arr.sort((a, b) => a.sort_order - b.sort_order);
  function build(parentId: string | null): TreeNode[] {
    return (byParent.get(parentId) ?? []).map((f) => ({
      folder: f,
      children: build(f.template_folder_id),
    }));
  }
  return build(null);
}

interface FlatNode {
  folder: BinderTemplateFolderRow;
  depth: number;
  hasChildren: boolean;
}

function flattenTree(
  tree: TreeNode[],
  openSet: Set<string>,
  depth = 0,
  out: FlatNode[] = [],
): FlatNode[] {
  for (const n of tree) {
    out.push({ folder: n.folder, depth, hasChildren: n.children.length > 0 });
    if (openSet.has(n.folder.template_folder_id) && n.children.length > 0) {
      flattenTree(n.children, openSet, depth + 1, out);
    }
  }
  return out;
}

/** Descendants (inclusive of self) — used for cycle guard on drop. */
function descendantsOf(folderId: string, all: BinderTemplateFolderRow[]): Set<string> {
  const s = new Set<string>([folderId]);
  const stack = [folderId];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const f of all) {
      if (f.parent_template_folder_id === cur && !s.has(f.template_folder_id)) {
        s.add(f.template_folder_id);
        stack.push(f.template_folder_id);
      }
    }
  }
  return s;
}

/**
 * Compute (new_parent, new_sort_order) for a fractional insert.
 * Given the siblings-list under new_parent (already sorted by sort_order),
 * insertion at index i means:
 *   i === 0                          -> new = firstSort - 1
 *   i === siblings.length            -> new = lastSort + 1
 *   0 < i < siblings.length          -> new = (siblings[i-1] + siblings[i]) / 2
 * Empty siblings list -> new = 0.
 */
function fractionalSortOrder(siblings: BinderTemplateFolderRow[], insertIndex: number): number {
  if (siblings.length === 0) return 0;
  if (insertIndex <= 0) return siblings[0]!.sort_order - 1;
  if (insertIndex >= siblings.length) return siblings[siblings.length - 1]!.sort_order + 1;
  const before = siblings[insertIndex - 1]!.sort_order;
  const after = siblings[insertIndex]!.sort_order;
  const mid = (before + after) / 2;
  if (mid - before < 1e-9 || after - mid < 1e-9) {
    return before + Math.max(1, Math.floor((after - before) / 2));
  }
  return mid;
}

// Row visual constants. Kept in JS so the drop-indicator overlays can share
// the exact same math (avoids "line is 4px off" bugs across CSS/JS).
// FOLDER names. Mirrors SAFE_FOLDER_NAME_RE in the backend's
// handlers/admin/binderTemplates/_schema.ts so the user sees the rejection
// immediately instead of after a round trip — keep the two in step.
//
// The TMF layout genuinely names folders "IRB/IEC" and "INITIAL
// APPLICATION/APPROVAL", so a slash is part of the name, not a path. Where a
// folder name can reach an S3 key the separator is folded in
// services/eisf/s3FolderPaths.ts::s3PathSegment. What stays banned is AWS's
// own "characters to avoid" list, plus control characters.
// Control-char range is deliberate, not a typo — eslint-disable justified.
// eslint-disable-next-line no-control-regex
const SAFE_NAME_RE = /^[^\x00-\x1F\x7F-\x9F\\{}^%`\]"><[~#|]+$/;

/** Spelled out: "invalid character" tells nobody what they may type. */
const FOLDER_NAME_MESSAGE =
  'Folder names may use letters, numbers, spaces and ! $ & \' ( ) * + , - . / : ; = ? @ _ — '
  + 'but not \\ { } ^ % ` ] " > [ ~ # | or control characters.';

const ROW_HEIGHT_PX = 32;
const INDENT_STEP_PX = 20; // per depth level
const ROW_LEFT_PAD_PX = 12;
const CHEVRON_COL_PX = 18;

/**
 * CR-2026-09-29 — Mandatory documents panel.
 *
 * The problem this solves: auto-filing used to find its destination by
 * matching DIA folder names, so a sponsor who designed their own binder with
 * their own names got no match and the document was silently never filed.
 * Here the admin states, per mandatory document, which of THEIR folders holds
 * it. That single answer drives both where the document appears in the binder
 * and which S3 prefix the upload is written to.
 *
 * Deliberately a dropdown per document rather than a picker per folder: the
 * list of documents is short and fixed, the list of folders is long and
 * sponsor-specific, and this way an unmapped document is impossible to miss.
 */
/** The catalogue code of the Delegation of Authority document type. */
const DOA_CODE = 'delegation-of-authority';

function MandatoryDocumentsPanel({
  documents,
  readiness,
  folders,
  mastersById,
  readOnly,
  isPending,
  onMap,
  title = 'Mandatory documents',
  intro = 'Choose which folder of this template each document is filed into. Uploads then land in that folder automatically, wherever they were uploaded from, and the file itself is stored under that folder in the bucket. Name the folders whatever you like.',
  warning = 'Until every document below has a folder, those uploads will not file into a binder built from this template.',
}: {
  title?: string;
  intro?: string;
  warning?: string;
  documents: MandatoryDocument[];
  readiness: BinderTemplateReadiness | undefined;
  folders: BinderTemplateFolderRow[];
  mastersById: Map<string, MasterFolderTemplateRow>;
  readOnly: boolean;
  isPending: boolean;
  onMap: (code: string, fromFolderId: string | null, toFolderId: string | null) => void;
}) {
  // Only leaves can hold documents: a folder with children is a zone or
  // section heading, and the backend refuses to map one.
  const selectable = useMemo(() => {
    const parents = new Set(
      folders.map((f) => f.parent_template_folder_id).filter(Boolean) as string[],
    );
    return folders
      .filter((f) => !parents.has(f.template_folder_id))
      .map((f) => ({
        id: f.template_folder_id,
        label:
          f.display_name_override
          ?? mastersById.get(f.master_folder_template_id ?? '')?.artifact_type
          ?? '(unnamed folder)',
        zone: mastersById.get(f.master_folder_template_id ?? '')?.zone_name ?? null,
        isLocal: f.is_local === 'true',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [folders, mastersById]);

  if (documents.length === 0) return null;
  const ready = readiness?.ready ?? documents.every((d) => d.mapped_template_folder_id);

  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-surface-border px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink-primary">
            {title} ({documents.length})
          </h2>
          <p className="text-xs text-ink-muted">{intro}</p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            ready ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {ready ? 'Ready' : `${readiness?.missing.length ?? 0} unmapped`}
        </span>
      </div>

      {!ready && (
        <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50/60 px-4 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{warning}</span>
        </div>
      )}

      <ul className="divide-y divide-surface-border/60">
        {documents.map((d) => (
          <li key={d.code} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm text-ink-primary">
                {d.mapped_template_folder_id
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                <span className="font-medium">{d.name}</span>
              </div>
              {(d.zone_name || d.section_name || d.artifact_type) && (
                <div className="ml-5 text-[11px] text-ink-muted">
                  Standard location: {[d.zone_name, d.section_name, d.artifact_type].filter(Boolean).join(' / ')}
                </div>
              )}
            </div>
            <select
              className="h-7 min-w-[16rem] rounded border border-surface-border bg-surface-card px-1 text-xs text-ink-secondary disabled:opacity-50"
              value={d.mapped_template_folder_id ?? ''}
              disabled={readOnly || isPending}
              title={readOnly ? 'This template is read-only' : `Folder that holds ${d.name}`}
              onChange={(e) =>
                onMap(d.code, d.mapped_template_folder_id, e.target.value || null)
              }
            >
              <option value="">— not mapped —</option>
              {selectable.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}{f.zone ? ` (${f.zone})` : ''}{f.isLocal ? ' · custom' : ''}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FolderTree({
  folders,
  mastersById,
  mandatoryArtifactTypes,
  masters,
  readOnly,
  onRename,
  onMove,
  onDelete,
  onAddChild,
  onSetTier,
  onRemapMaster,
  docTypes,
  docTypesByCode,
  onPickDocType,
  onOpenApproval,
}: {
  folders: BinderTemplateFolderRow[];
  mastersById: Map<string, MasterFolderTemplateRow>;
  mandatoryArtifactTypes: Set<string>;
  masters: MasterFolderTemplateRow[];
  readOnly: boolean;
  onRename: (folderId: string, name: string) => void;
  onMove: (folderId: string, parentId: string | null, sortOrder?: number) => void;
  onDelete: (folderId: string) => void;
  onAddChild: (parentId: string) => void;
  onSetTier: (folderId: string, tier: 'common' | 'unblinded_only' | 'general') => void;
  onRemapMaster: (folderId: string, masterId: string | null) => void;
  docTypes: DocumentTypeWithApprovers[];
  docTypesByCode: Map<string, DocumentTypeWithApprovers>;
  onPickDocType: (folderId: string, code: string | null) => void;
  onOpenApproval: (folderId: string) => void;
}) {
  const tree = useMemo(() => buildTree(folders), [folders]);
  const allIds = useMemo(() => folders.map((f) => f.template_folder_id), [folders]);
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(allIds));
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  function toggleOpen(id: string) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function expandAll() {
    setOpenIds(new Set(allIds));
  }
  function collapseAll() {
    setOpenIds(new Set());
  }

  // When a filter is applied, we render a FLAT visible-set (matches only)
  // rather than the tree. Depth is preserved so hierarchy stays legible,
  // but DnD is disabled because reordering a filtered subset produces
  // ambiguous sort_order semantics.
  const flat = useMemo<FlatNode[]>(() => {
    const q = filter.trim().toLowerCase();
    const full = flattenTree(tree, openIds);
    if (!q) return full;
    const flatAll = flattenTree(tree, new Set(allIds)); // ignore collapse when filtering
    return flatAll.filter((n) => {
      const master = n.folder.master_folder_template_id
        ? mastersById.get(n.folder.master_folder_template_id)
        : null;
      const label = n.folder.display_name_override
        ?? master?.artifact_type
        ?? '';
      return label.toLowerCase().includes(q);
    });
  }, [tree, openIds, allIds, filter, mastersById]);

  const dndDisabled = readOnly || filter.trim().length > 0;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{
    targetId: string | 'root';
    position: DropPosition;
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }

  function handleDragOver(e: DragOverEvent) {
    const overId = e.over?.id ? String(e.over.id) : null;
    if (!overId) {
      setDropHint(null);
      return;
    }
    if (overId === 'root-drop') {
      setDropHint({ targetId: 'root', position: 'root' });
      return;
    }
    const overRect = e.over?.rect;
    const activeRect = e.active.rect.current.translated;
    if (!overRect || !activeRect) {
      setDropHint({ targetId: overId, position: 'below' });
      return;
    }
    const draggedCenterY = activeRect.top + activeRect.height / 2;
    const relY = draggedCenterY - overRect.top;
    const third = overRect.height / 3;
    let position: DropPosition;
    if (relY < third) position = 'above';
    else if (relY > third * 2) position = 'below';
    else position = 'into';
    setDropHint({ targetId: overId, position });
  }

  function handleDragEnd(e: DragEndEvent) {
    const activeIdStr = String(e.active.id);
    const hint = dropHint;
    setActiveId(null);
    setDropHint(null);
    if (!hint) return;

    const dragged = folders.find((f) => f.template_folder_id === activeIdStr);
    if (!dragged) return;

    let newParentId: string | null;
    let newSortOrder: number;

    if (hint.targetId === 'root' && hint.position === 'root') {
      const rootSiblings = folders
        .filter((f) => (f.parent_template_folder_id ?? null) === null
          && f.template_folder_id !== activeIdStr)
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order);
      newParentId = null;
      newSortOrder = fractionalSortOrder(rootSiblings, rootSiblings.length);
    } else {
      const target = folders.find((f) => f.template_folder_id === hint.targetId);
      if (!target) return;
      const forbidden = descendantsOf(activeIdStr, folders);
      if (forbidden.has(target.template_folder_id)) {
        toast.error('Cannot move a folder underneath itself.');
        return;
      }
      if (hint.position === 'into') {
        newParentId = target.template_folder_id;
        const childrenOfTarget = folders
          .filter((f) => f.parent_template_folder_id === target.template_folder_id
            && f.template_folder_id !== activeIdStr)
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order);
        newSortOrder = fractionalSortOrder(childrenOfTarget, childrenOfTarget.length);
        setOpenIds((prev) => new Set(prev).add(target.template_folder_id));
      } else {
        newParentId = target.parent_template_folder_id ?? null;
        const siblings = folders
          .filter((f) => (f.parent_template_folder_id ?? null) === (newParentId ?? null)
            && f.template_folder_id !== activeIdStr)
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order);
        const idxOfTarget = siblings.findIndex(
          (f) => f.template_folder_id === target.template_folder_id,
        );
        const insertIndex = hint.position === 'above' ? idxOfTarget : idxOfTarget + 1;
        newSortOrder = fractionalSortOrder(siblings, insertIndex);
      }
    }

    const currentParent = dragged.parent_template_folder_id ?? null;
    if (currentParent === newParentId && dragged.sort_order === newSortOrder) {
      return;
    }
    onMove(activeIdStr, newParentId, newSortOrder);
    toast.success('Folder moved');
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-surface-border bg-surface-muted/30 px-3 py-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-muted" />
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter folders by name…"
            className="input w-full h-8 pl-8 text-sm"
          />
        </div>
        <button
          type="button"
          className="btn-secondary h-8 text-xs"
          onClick={expandAll}
          title="Expand all folders"
        >
          <ChevronsUpDown className="h-3.5 w-3.5" /> Expand all
        </button>
        <button
          type="button"
          className="btn-secondary h-8 text-xs"
          onClick={collapseAll}
          title="Collapse all folders"
        >
          <ChevronsDownUp className="h-3.5 w-3.5" /> Collapse all
        </button>
      </div>

      {folders.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-ink-muted">
          This template has no folders yet.
        </div>
      ) : flat.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-ink-muted">
          No folders match "{filter.trim()}".
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveId(null);
            setDropHint(null);
          }}
        >
          <SortableContext
            items={flat.map((n) => n.folder.template_folder_id)}
            strategy={verticalListSortingStrategy}
          >
            {!dndDisabled && activeId && (
              <RootDropZone hinted={dropHint?.targetId === 'root'} />
            )}
            <ul className="list-none py-1">
              {flat.map((n) => (
                <FolderNodeRow
                  key={n.folder.template_folder_id}
                  node={n}
                  openSet={openIds}
                  onToggleOpen={toggleOpen}
                  mastersById={mastersById}
                  mandatoryArtifactTypes={mandatoryArtifactTypes}
                  masters={masters}
                  allFolders={folders}
                  readOnly={readOnly}
                  dndDisabled={dndDisabled}
                  activeId={activeId}
                  dropHint={dropHint}
                  selected={selectedId === n.folder.template_folder_id}
                  onSelect={setSelectedId}
                  docTypes={docTypes}
                  docTypesByCode={docTypesByCode}
                  onPickDocType={onPickDocType}
                  onOpenApproval={onOpenApproval}
                  onRename={onRename}
                  onMove={onMove}
                  onDelete={onDelete}
                  onAddChild={onAddChild}
                  onSetTier={onSetTier}
                  onRemapMaster={onRemapMaster}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </>
  );
}

function RootDropZone({ hinted }: { hinted: boolean }) {
  const { setNodeRef, isOver } = useSortable({ id: 'root-drop' });
  const active = hinted || isOver;
  return (
    <div
      ref={setNodeRef}
      className={`flex items-center justify-center gap-2 mx-3 my-1 rounded border-2 border-dashed py-1.5 text-[11px] uppercase tracking-wider transition ${
        active
          ? 'border-accent-500 bg-accent-50 text-accent-700'
          : 'border-surface-border/60 text-ink-muted'
      }`}
    >
      <ArrowLeft className="h-3 w-3 rotate-90" />
      Drop here to move to root
    </div>
  );
}

function FolderNodeRow({
  node,
  openSet,
  onToggleOpen,
  mastersById,
  mandatoryArtifactTypes,
  masters,
  allFolders,
  readOnly,
  dndDisabled,
  activeId,
  dropHint,
  selected,
  onSelect,
  onRename,
  onMove,
  onDelete,
  onAddChild,
  onSetTier,
  onRemapMaster,
  docTypes,
  docTypesByCode,
  onPickDocType,
  onOpenApproval,
}: {
  node: FlatNode;
  openSet: Set<string>;
  onToggleOpen: (id: string) => void;
  mastersById: Map<string, MasterFolderTemplateRow>;
  mandatoryArtifactTypes: Set<string>;
  masters: MasterFolderTemplateRow[];
  allFolders: BinderTemplateFolderRow[];
  readOnly: boolean;
  dndDisabled: boolean;
  activeId: string | null;
  dropHint: { targetId: string | 'root'; position: DropPosition } | null;
  selected: boolean;
  onSelect: (id: string) => void;
  onRename: (folderId: string, name: string) => void;
  onMove: (folderId: string, parentId: string | null, sortOrder?: number) => void;
  onDelete: (folderId: string) => void;
  onAddChild: (parentId: string) => void;
  onSetTier: (folderId: string, tier: 'common' | 'unblinded_only' | 'general') => void;
  onRemapMaster: (folderId: string, masterId: string | null) => void;
  docTypes: DocumentTypeWithApprovers[];
  docTypesByCode: Map<string, DocumentTypeWithApprovers>;
  onPickDocType: (folderId: string, code: string | null) => void;
  onOpenApproval: (folderId: string) => void;
}) {
  const { folder, depth, hasChildren } = node;
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [hovered, setHovered] = useState(false);
  const open = openSet.has(folder.template_folder_id);
  const mappedCode = parseMappedDocTypeCodes(folder)[0] ?? null;
  const approval = approvalSummary(mappedCode ? docTypesByCode.get(mappedCode) : undefined);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: folder.template_folder_id,
    disabled: dndDisabled,
  });

  const master = folder.master_folder_template_id
    ? mastersById.get(folder.master_folder_template_id)
    : null;
  const displayName =
    folder.display_name_override ?? master?.artifact_type ?? '(unnamed folder)';
  const originalName = master?.artifact_type;
  const isRenamed = !!folder.display_name_override
    && !!originalName
    && folder.display_name_override !== originalName;
  const isLocal = folder.is_local === 'true';
  const isMandatory = !!master && mandatoryArtifactTypes.has(master.artifact_type);

  const descendantIds = useMemo(
    () => descendantsOf(folder.template_folder_id, allFolders),
    [folder.template_folder_id, allFolders],
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const hintingHere = dropHint?.targetId === folder.template_folder_id;
  const showAboveLine = hintingHere && dropHint?.position === 'above';
  const showBelowLine = hintingHere && dropHint?.position === 'below';
  const showIntoOutline = hintingHere && dropHint?.position === 'into';

  const paddingLeft = ROW_LEFT_PAD_PX + depth * INDENT_STEP_PX;

  function beginRename() {
    if (readOnly) return;
    setDraftName(displayName === '(unnamed folder)' ? '' : displayName);
    setRenaming(true);
  }
  function commitRename() {
    const trimmed = draftName.trim();
    if (trimmed && !SAFE_NAME_RE.test(trimmed)) {
      toast.error(FOLDER_NAME_MESSAGE);
      return;
    }
    onRename(folder.template_folder_id, trimmed);
    setRenaming(false);
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="relative list-none"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Indent guide lines - one per depth level. Subtle, dashed. */}
      {depth > 0 && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 flex"
          style={{ left: ROW_LEFT_PAD_PX }}
        >
          {Array.from({ length: depth }).map((_, i) => (
            <div
              key={i}
              className="border-l border-dashed border-surface-border/70"
              style={{ width: INDENT_STEP_PX }}
            />
          ))}
        </div>
      )}

      {/* Drop indicator: sibling-above line */}
      {showAboveLine && (
        <div
          className="pointer-events-none absolute top-0 right-2 h-[3px] rounded-full bg-accent-500 z-20"
          style={{ left: paddingLeft }}
        />
      )}

      <div
        className={[
          'group relative flex items-center gap-1.5 pr-2 transition-colors',
          selected ? 'bg-accent-50/60' : 'hover:bg-surface-muted/40',
          showIntoOutline ? 'ring-2 ring-accent-500 ring-inset bg-accent-50/70' : '',
        ].join(' ')}
        style={{ paddingLeft, height: ROW_HEIGHT_PX }}
        onClick={() => onSelect(folder.template_folder_id)}
      >
        {/* Grip handle: hover-visible so it doesn't crowd resting state */}
        {!dndDisabled && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className={[
              'shrink-0 cursor-grab text-ink-muted hover:text-ink-primary active:cursor-grabbing touch-none',
              hovered || isDragging ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
            title="Drag to reorder or nest"
            aria-label="Drag handle"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Chevron / spacer, fixed-width so folder icons align across depths */}
        <div className="shrink-0" style={{ width: CHEVRON_COL_PX }}>
          {hasChildren ? (
            <button
              type="button"
              className="text-ink-muted hover:text-ink-primary"
              onClick={(e) => {
                e.stopPropagation();
                onToggleOpen(folder.template_folder_id);
              }}
              title={open ? 'Collapse' : 'Expand'}
            >
              {open ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : null}
        </div>

        {/* Folder icon: open when expanded, plain when leaf/collapsed */}
        {hasChildren && open ? (
          <FolderOpen
            className={`h-4 w-4 shrink-0 ${isLocal ? 'text-warning-600' : 'text-accent-600'}`}
          />
        ) : (
          <Folder
            className={`h-4 w-4 shrink-0 ${isLocal ? 'text-warning-600' : 'text-ink-muted'}`}
          />
        )}

        {/* Name: double-click to rename; F2 keyboard also works when row selected */}
        {renaming ? (
          <div
            className="flex flex-1 items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              autoFocus
              value={draftName}
              maxLength={255}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setRenaming(false);
              }}
              className="input h-6 text-sm flex-1 min-w-0"
            />
          </div>
        ) : (
          <>
            <span
              className="truncate text-sm text-ink-primary select-none"
              onDoubleClick={(e) => {
                e.stopPropagation();
                beginRename();
              }}
              onKeyDown={(e) => {
                if ((e.key === 'F2' || e.key === 'Enter') && selected && !readOnly) {
                  beginRename();
                }
              }}
              tabIndex={0}
              title={readOnly ? displayName : 'Double-click to rename'}
            >
              {displayName}
            </span>
            {isRenamed && (
              <span className="shrink-0 text-[11px] text-ink-muted italic">
                (was "{originalName}")
              </span>
            )}
            {isLocal && (
              <span className="shrink-0 rounded bg-warning-100 px-1.5 py-0.5 text-[10px] font-medium text-warning-800">
                Custom
              </span>
            )}
            {isMandatory && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-800"
                title="Required document — cannot be removed from any binder template."
              >
                <Lock className="h-3 w-3" /> Required
              </span>
            )}
            {!hasChildren && mappedCode && (
              <span
                className={`hidden shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium lg:inline ${
                  approval.enabled
                    ? 'bg-success-100 text-success-800'
                    : 'bg-surface-muted text-ink-muted'
                }`}
                title={`${docTypesByCode.get(mappedCode)?.document_type_name ?? mappedCode} · ${approval.label}`}
              >
                {approval.enabled ? 'Approval on' : 'Approval off'}
              </span>
            )}
          </>
        )}

        {!renaming && !readOnly && (
          <div
            className={[
              'ml-auto flex shrink-0 items-center gap-0.5 transition-opacity',
              hovered ? 'opacity-100' : 'opacity-0 group-focus-within:opacity-100',
            ].join(' ')}
            onClick={(e) => e.stopPropagation()}
          >
            <IconAction
              onClick={() => onAddChild(folder.template_folder_id)}
              title="Add child folder"
            >
              <Plus className="h-3.5 w-3.5" />
            </IconAction>
            {/* CR-2026-09-29 — re-point an existing folder at a different
                standard folder. Previously the only way was to delete and
                re-add, which lost the folder's children and its document
                routing. The display name is kept deliberately. */}
            <select
              value={folder.master_folder_template_id ?? ''}
              onChange={(e) => onRemapMaster(folder.template_folder_id, e.target.value || null)}
              className="ml-1 h-6 max-w-[9rem] rounded border border-surface-border bg-surface-card px-1 text-[11px] text-ink-secondary"
              title="Standard folder this maps to — controls the zone and section it files under"
            >
              <option value="">— custom —</option>
              {masters
                .filter((m) => m.is_active || m.template_id === folder.master_folder_template_id)
                .map((m) => (
                  <option key={m.template_id} value={m.template_id}>
                    {m.artifact_type}
                  </option>
                ))}
            </select>
            <select
              value={folder.tier === 'common' || folder.tier === 'unblinded_only' ? folder.tier : 'general'}
              onChange={(e) =>
                onSetTier(folder.template_folder_id, e.target.value as 'common' | 'unblinded_only' | 'general')
              }
              className="ml-1 h-6 rounded border border-surface-border bg-surface-card px-1 text-[11px] text-ink-secondary"
              title="Blinding tier — who sees this folder on a blinded study"
            >
              <option value="general">General</option>
              <option value="common">Common</option>
              <option value="unblinded_only">Unblinded only</option>
            </select>
            {!isMandatory && (
              <IconAction
                onClick={() => onDelete(folder.template_folder_id)}
                title="Remove from this template"
                disabled={hasChildren}
                danger
              >
                <Trash2 className="h-3.5 w-3.5" />
              </IconAction>
            )}
            {/* Keyboard-accessible parent picker - dnd-kit's keyboard sensor
                does re-parent too, but a plain <select> is the most reliable
                fallback for screen-reader users. Compact so it doesn't dominate. */}
            <select
              value={folder.parent_template_folder_id ?? ''}
              onChange={(e) =>
                onMove(
                  folder.template_folder_id,
                  e.target.value ? e.target.value : null,
                )
              }
              className="ml-1 h-6 rounded border border-surface-border bg-surface-card px-1 text-[11px] text-ink-secondary"
              title="Move under a different parent"
            >
              <option value="">— root —</option>
              {allFolders
                .filter((f) => !descendantIds.has(f.template_folder_id))
                .map((f) => {
                  const label =
                    f.display_name_override
                    ?? mastersById.get(f.master_folder_template_id ?? '')?.artifact_type
                    ?? '(unnamed)';
                  return (
                    <option key={f.template_folder_id} value={f.template_folder_id}>
                      {label}
                    </option>
                  );
                })}
            </select>
          </div>
        )}
      </div>

      {selected && !hasChildren && (
        <div
          className="flex flex-wrap items-center gap-3 border-y border-accent-200 bg-accent-50/40 py-2 pr-3"
          style={{ paddingLeft }}
          onClick={(e) => e.stopPropagation()}
        >
          {!isLocal && (
            <label className="flex items-center gap-1.5 text-[11px] text-ink-secondary">
              <span className="uppercase tracking-wider text-ink-muted">Document type</span>
              <select
                value={mappedCode ?? ''}
                onChange={(e) => onPickDocType(folder.template_folder_id, e.target.value || null)}
                disabled={readOnly}
                className="h-7 w-72 rounded border border-surface-border bg-surface-card px-1.5 text-xs text-ink-primary"
              >
                <option value="">— none —</option>
                {docTypes
                  .filter((t) => t.is_active || t.document_type_code === mappedCode)
                  .map((t) => (
                    <option key={t.document_type_code} value={t.document_type_code}>
                      {t.document_type_name}
                    </option>
                  ))}
              </select>
            </label>
          )}

          <button
            type="button"
            disabled={readOnly}
            onClick={() => onOpenApproval(folder.template_folder_id)}
            className={`h-7 rounded border px-2 text-[11px] ${
              readOnly
                ? 'cursor-not-allowed border-dashed border-surface-border bg-surface-muted/40 text-ink-muted'
                : approval.enabled
                ? 'border-success-300 bg-success-50 text-success-700'
                : 'border-surface-border bg-surface-card text-ink-secondary'
            }`}
            title={
              readOnly
                ? 'This template is read-only, so its approvers cannot be changed here'
                : 'Set who must approve documents filed into this folder'
            }
          >
            {mappedCode ? `Approvers: ${approval.label}` : 'Set approvers'}
          </button>
        </div>
      )}

      {/* Drop indicator: sibling-below line */}
      {showBelowLine && (
        <div
          className="pointer-events-none absolute bottom-0 right-2 h-[3px] rounded-full bg-accent-500 z-20"
          style={{ left: paddingLeft }}
        />
      )}

      {/* activeId highlight (ghost of dragged row) */}
      {activeId === folder.template_folder_id && (
        <div className="pointer-events-none absolute inset-0 ring-2 ring-accent-400 rounded-sm" />
      )}
    </li>
  );
}

function IconAction({
  children,
  onClick,
  title,
  disabled = false,
  danger = false,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      title={title}
      className={[
        'inline-flex h-6 w-6 items-center justify-center rounded transition',
        disabled
          ? 'cursor-not-allowed text-ink-muted/40'
          : danger
            ? 'text-danger-600 hover:bg-danger-50'
            : 'text-ink-muted hover:bg-surface-muted hover:text-ink-primary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// AddFolderModal
// ---------------------------------------------------------------------------

function AddFolderModal({
  onClose,
  parentId,
  folders,
  masters,
  usedMasterIds,
  isPending,
  onSubmit,
}: {
  onClose: () => void;
  parentId: string | null;
  folders: BinderTemplateFolderRow[];
  masters: MasterFolderTemplateRow[];
  usedMasterIds: Set<string>;
  isPending: boolean;
  onSubmit: (body: {
    master_folder_template_id?: string;
    display_name_override?: string;
    parent_template_folder_id?: string | null;
  }) => void;
}) {
  const [pickedMaster, setPickedMaster] = useState<string>('');
  const [override, setOverride] = useState('');

  const availableMasters = useMemo(
    () =>
      masters
        .filter((m) => m.is_active)
        .filter((m) => !usedMasterIds.has(m.template_id))
        .slice()
        .sort((a, b) => a.artifact_type.localeCompare(b.artifact_type)),
    [masters, usedMasterIds],
  );

  const parentLabel = parentId
    ? folders.find((f) => f.template_folder_id === parentId)?.display_name_override
      ?? masters.find(
        (m) =>
          m.template_id ===
          folders.find((f) => f.template_folder_id === parentId)?.master_folder_template_id,
      )?.artifact_type
      ?? '(unnamed)'
    : 'Root';

  function submit() {
    if (!pickedMaster && !override.trim()) {
      toast.error('Pick a standard folder or give this new folder a name.');
      return;
    }
    if (override.trim() && !SAFE_NAME_RE.test(override.trim())) {
      toast.error(FOLDER_NAME_MESSAGE);
      return;
    }
    onSubmit({
      master_folder_template_id: pickedMaster || undefined,
      display_name_override: override.trim() || undefined,
      parent_template_folder_id: parentId,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
          <h2 className="text-lg font-semibold text-ink-primary">
            Add folder under <span className="text-ink-muted">{parentLabel}</span>
          </h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="label">Standard folder</label>
            <select
              value={pickedMaster}
              onChange={(e) => setPickedMaster(e.target.value)}
              className="input w-full"
            >
              <option value="">— None (custom to this template) —</option>
              {availableMasters.map((m) => (
                <option key={m.template_id} value={m.template_id}>
                  {m.artifact_type}
                  {m.zone_name ? ` (${m.zone_name})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-muted">
              Pick a standard folder so uploads auto-file into it. Leave as None to create a
              folder that only exists in this template.
            </p>
          </div>
          <div>
            <label className="label">
              Display name {pickedMaster ? '(optional)' : '(required)'}
            </label>
            <input
              type="text"
              value={override}
              maxLength={255}
              onChange={(e) => setOverride(e.target.value)}
              className="input w-full"
              placeholder={
                pickedMaster
                  ? 'Leave blank to keep the master name'
                  : 'e.g. Sponsor Attestations'
              }
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-surface-border px-5 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <Plus className="h-4 w-4" /> Add folder
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Punch-list f22 — version-history log. Every template-level and
 * folder-level change already writes an audit row keyed to this
 * template's id (see history.ts's handler doc); this just renders them.
 */
const HISTORY_ACTION_LABEL: Record<string, string> = {
  'master_data.binder_template.created': 'Template created',
  'master_data.binder_template.updated': 'Template renamed / updated',
  'master_data.binder_template.deactivated': 'Template deactivated',
  'master_data.binder_template.reactivated': 'Template restored',
  'master_data.binder_template.folder.added': 'Folder added',
  'master_data.binder_template.folder.renamed': 'Folder renamed',
  'master_data.binder_template.folder.moved': 'Folder moved',
  'master_data.binder_template.folder.removed': 'Folder removed',
};

function HistoryDrawer({
  templateId,
  templateName,
  onClose,
}: {
  templateId: string;
  templateName: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = useQuery({
    queryKey: ['adminBinderTemplates', 'history', templateId],
    queryFn: () => adminBinderTemplates.history(templateId),
  });
  const events = q.data?.events ?? [];

  function summarise(newValue: string | null): string | null {
    if (!newValue) return null;
    try {
      const parsed = JSON.parse(newValue) as Record<string, unknown>;
      const parts = Object.entries(parsed)
        .filter(([k]) => k !== 'template_folder_id')
        .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`);
      return parts.join(', ');
    } catch {
      return newValue;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col border-l border-surface-border bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-ink-muted">History</div>
            <h2 className="truncate text-base font-semibold text-ink-primary">{templateName}</h2>
          </div>
          <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {q.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
            </div>
          ) : events.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-muted">No history yet.</p>
          ) : (
            <ol className="space-y-3 border-l border-surface-border pl-4">
              {events.map((e) => (
                <li key={e.audit_id} className="relative">
                  <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-accent-500" />
                  <div className="text-sm font-medium text-ink-primary">
                    {HISTORY_ACTION_LABEL[e.action] ?? e.action}
                  </div>
                  <div className="text-[11px] text-ink-muted">
                    {fmtDateTime(e.timestamp)} · {e.user_name || 'system'}
                  </div>
                  {summarise(e.new_value) && (
                    <p className="mt-0.5 truncate text-[11px] text-ink-secondary" title={summarise(e.new_value) ?? undefined}>
                      {summarise(e.new_value)}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewDrawer({
  templateName,
  folders,
  mastersById,
  onClose,
}: {
  templateName: string;
  folders: BinderTemplateFolderRow[];
  mastersById: Map<string, MasterFolderTemplateRow>;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tree = useMemo(() => buildTree(folders), [folders]);

  function label(f: BinderTemplateFolderRow): string {
    const master = f.master_folder_template_id
      ? mastersById.get(f.master_folder_template_id)
      : null;
    return (f.display_name_override && f.display_name_override.length > 0)
      ? f.display_name_override
      : (master?.artifact_type || 'Unnamed folder');
  }

  function renderNode(node: TreeNode, depth: number): ReactNode {
    return (
      <li key={node.folder.template_folder_id}>
        <div
          className="flex items-center gap-2 py-1 text-sm text-ink-primary"
          style={{ paddingLeft: 8 + depth * 16 }}
        >
          {node.children.length > 0 ? (
            <FolderOpen className="h-4 w-4 shrink-0 text-accent-600" />
          ) : (
            <Folder className="h-4 w-4 shrink-0 text-ink-muted" />
          )}
          <span className="truncate">{label(node.folder)}</span>
        </div>
        {node.children.length > 0 && (
          <ul className="list-none">
            {node.children.map((c) => renderNode(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col border-l border-surface-border bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-ink-muted">Preview</div>
            <h2 className="truncate text-base font-semibold text-ink-primary">{templateName}</h2>
            <p className="text-[11px] text-ink-muted">Folders every new study binder will start with.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-primary"
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {tree.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-muted">This template has no folders yet.</p>
          ) : (
            <ul className="list-none">
              {tree.map((n) => renderNode(n, 0))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
